import { Delete, SpatialAudioOffOutlined } from "@mui/icons-material";
import { Box, Divider, IconButton, SvgIconTypeMap, Typography } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { useRef, useState } from "react";
import emitSnackbar from "../../../emitSnackbar";
import DragDrop from "../Layout/DragDrop";
import DragDropParent from "../Layout/DragDropParent";
import { StudyFile } from "../models";
import { useAddStudyStore } from "./useAddStudyStore";

export type UploadDocsComponentProps = {
  id: number;
  header?: string;
  respondentId?: string;
  audio_file_id?: string;
  Icon?: OverridableComponent<SvgIconTypeMap & any>;
  fromAudio?: boolean;
  acceptedFiles?: string;
  registerAudioFile: (file: File | null) => void;
  studyFile?: StudyFile;
};

export default function UploadDocsComponent({
  id,
  header,
  Icon,
  respondentId,
  audio_file_id,
  fromAudio = false,
  registerAudioFile,
  acceptedFiles,
  studyFile,
}: UploadDocsComponentProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const {
    checkForDuplicate,
    getFileExtension,
    getAudioFileName,
    getFileName,
    presentationFile,
    screenerSummaryFile,
    screenerGuideFile,
    discussionGuideFile,
  } = useAddStudyStore();

  const StyledIcon = ({ Icon }: { Icon: OverridableComponent<SvgIconTypeMap & any> }) => {
    return <Icon sx={{ width: "30px", height: "30px", color: "white" }} fontSize="medium" />;
  };

  const handleDelete = () => {
    setFile(null);
    registerAudioFile(null);
  };

  const handleClickBrowse = () => {
    inputRef.current?.click();
  };

  const handleFileSelect = (file?: File) => {
    if (file && !checkForDuplicate(file)) {
      setFile(file);
      registerAudioFile(file);
    } else {
      emitSnackbar(
        `This file ${file?.name} has been uploaded more than once. Please review uploaded files and make the necessary changes.`,
        "info",
        { autoHideDuration: 3000 },
      );
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles.length) return;

    const file = droppedFiles[0];
    const extension = getFileExtension(file.name)?.toLowerCase().replace(".", "");

    const allowedExtensions = {
      audio: ["mp3", "wav", "m4a", "flac", "aac", "wma", "ogg"],
      0: ["csv", "xml"],
      1: ["pdf", "doc", "docx"],
      2: ["pdf", "doc", "docx"],
      3: ["ppt", "pptx"],
    };

    const extensionsToCheck = fromAudio ? allowedExtensions.audio : allowedExtensions[id];

    if (!extensionsToCheck || extensionsToCheck.includes(extension)) {
      handleFileSelect(file);
      return;
    }

    // Check if file extension is allowed
    const allowedExtensionsText = extensionsToCheck.map((ext: string) => `.${ext}`).join(", ");
    emitSnackbar(`Only ${allowedExtensionsText} files are allowed`, "error", {
      autoHideDuration: 10000,
    });
  };

  const disableView = () => {
    const isPresentationSection: boolean = header === "Presentation";
    const hasPresentationFile: boolean = Boolean(presentationFile);
    const hasOtherFiles: boolean = Boolean(screenerSummaryFile || screenerGuideFile || discussionGuideFile);

    const isDisabled: boolean =
      (hasPresentationFile && !isPresentationSection) || (hasOtherFiles && isPresentationSection);

    return {
      opacity: isDisabled ? 0.5 : 1,
      cursor: isDisabled ? "not-allowed" : "pointer",
      pointerEvents: isDisabled ? "none" : "auto",
    };
  };

  const handleTemplateDownload = () => {
    const link = document.createElement("a");
    link.href = `/assets/screener_summary_template.csv`;
    link.download = "Screener_Summary_Template.csv"; // Name of the downloaded file
    link.click();
  };

  let file_name: string | undefined;

  if (file) {
    file_name = fromAudio ? getAudioFileName(file, audio_file_id!) : getFileName(file, id);
    // Only set file name value if a file has been selected
    file_name = file ? file_name : undefined;
  }

  return (
    <Box key={header} sx={disableView()}>
      {header === "Presentation" && <Divider sx={{ mt: "1rem", mb: "1.2rem" }} />}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontSize: "12px", fontWeight: "400" }}>
          {header}
        </Typography>
        {header === "Screener Summary" && (
          <Box
            sx={{ display: "flex", alignItems: "center", ml: "2px", cursor: "pointer" }}
            onClick={handleTemplateDownload}
          >
            -
            <Typography sx={{ fontSize: "12px", fontWeight: "400", textDecoration: "underline", ml: "2px" }}>
              download template
            </Typography>
          </Box>
        )}
      </Box>
      <DragDropParent hasFile={!!file} isDragging={isDragging}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flexBasis: file || !fromAudio ? "0%" : "40%" }}>
          <Box
            sx={{
              width: "3rem",
              height: "3rem",
              background: file ? " #00A3E0" : " rgba(0, 163, 224, 0.3)",
              display: "flex",
              borderRadius: "5px",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: "0",
            }}
          >
            <StyledIcon Icon={Icon || SpatialAudioOffOutlined} />
          </Box>
          {respondentId && !file && (
            <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: "400", color: "#13294B" }}>
              Audio ID: {audio_file_id}
            </Typography>
          )}
        </Box>
        {file_name ? (
          <Box
            sx={{
              display: "flex",
              flex: 1,
              justifyContent: "space-between",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                marginLeft: "10px",
                flexGrow: 1,
                flexShrink: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "16px",
                  fontWeight: "400",
                  color: "13294B",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "wrap",
                }}
              >
                {file_name}
              </Typography>
              {file && (
                <Typography variant="h6" sx={{ fontSize: "12px", fontWeight: "400", color: "13294B" }}>
                  Original file name: {file.name}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                marginLeft: "10px",
                flexShrink: 0,
                minWidth: "fit-content",
              }}
            >
              <IconButton color="primary" size="medium" onClick={handleDelete}>
                <Delete />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <DragDrop
            acceptedFiles={acceptedFiles!}
            inputRef={inputRef}
            onFileSelect={handleFileSelect}
            onClickBrowse={handleClickBrowse}
            setIsDragging={setIsDragging}
            handleDrop={handleDrop}
          />
        )}
      </DragDropParent>
    </Box>
  );
}
